import { useState, type FormEvent } from 'react';
import toast from 'react-hot-toast';
import { Mail } from 'lucide-react';
import { apiService } from '@/api/apiService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useDashboardSite } from '@/pages/dashboard/DashboardSiteContext';

export default function DashboardContactAdminPage() {
  const { userEmail, selectedSite, selectedSiteId } = useDashboardSite();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await apiService.dashboardContactAdmin({
        subject: subject.trim(),
        message: message.trim(),
        site_id: selectedSiteId ?? undefined,
      });
      toast.success("Message sent. We'll email you back at your account address.");
      setSubject('');
      setMessage('');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send your message. Try again later.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Contact admin</h2>
        <p className="text-sm text-muted-foreground">
          Billing, crawl, CRM, or setup questions — send a message and we&apos;ll reply to your account email.
        </p>
      </div>

      <Card className="border-primary/25 bg-primary/[0.03]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4 text-primary" aria-hidden />
            Message the platform team
          </CardTitle>
          <CardDescription>
            We reply to{' '}
            <strong className="text-foreground">{userEmail || 'your account email'}</strong>
            {selectedSite?.name ? (
              <>
                {' '}
                · site: <strong className="text-foreground">{selectedSite.name}</strong>
              </>
            ) : null}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="contact-admin-subject">Subject (optional)</Label>
              <Input
                id="contact-admin-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                placeholder="Billing, crawl stuck, CRM…"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-admin-message">Message</Label>
              <Textarea
                id="contact-admin-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                minLength={10}
                maxLength={4000}
                rows={6}
                placeholder="What happened, and what do you need?"
              />
            </div>
            <Button type="submit" disabled={sending || message.trim().length < 10}>
              {sending ? 'Sending…' : 'Send message'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
